import { useMemo, useState } from "react";

import { Link } from "react-router-dom";

import { ChevronDown, Search, Wallet, X } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { isApCreditNote } from "~/constants/siiDocTypes";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useSupplierPayableDocuments } from "~/hooks/purchasing";
import { usePagination } from "~/hooks/usePagination";
import { formatCurrency, formatDate, formatRut } from "~/utils/helpers";

import { PagarDocumentosDialog } from "./components/PagarDocumentosDialog";

import type { PayableDocument } from "~/types/purchasing";

const ESTADO_CHIP: Record<string, { label: string; className: string }> = {
  vencida: { label: "Vencida", className: "bg-red-100 text-red-800" },
  por_vencer: { label: "Por vencer", className: "bg-gray-100 text-gray-700" },
  sin_fecha: { label: "Sin fecha", className: "bg-gray-100 text-gray-500" },
  credito: { label: "Crédito", className: "bg-green-100 text-green-800" },
};

const keyOf = (d: PayableDocument) => `${d.source}:${d.doc_id}`;

// La columna Saldo sigue la convención del sub-mayor acordada con finanzas (igual que Facturas de
// Proveedor): negativo = por pagar (factura), positivo = crédito (NC). El RPC entrega payable_clp con
// el signo de pago invertido (deuda > 0 para la nómina), así que lo volvemos a firmar SOLO para
// mostrar. La lógica de pago (PagarDocumentosDialog) sigue usando payable_clp sin cambios.
const displaySaldo = (d: PayableDocument) => -Number(d.payable_clp);

// Una NC (nota de crédito) llega con payable_clp negativo → se muestra en positivo/verde (crédito);
// una factura por pagar se muestra en negativo.
const isCredit = (d: PayableDocument) => Number(d.payable_clp) < 0;
const origenLabel = (d: PayableDocument) =>
  isApCreditNote(d.doc_tipo_code)
    ? "Nota de crédito"
    : d.source === "opening"
      ? "Saldo inicial"
      : "Factura";

export default function PagosProveedorPage() {
  const [company, setCompany] = useState<string>(ALIACE_SPA_ID);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState<string>("all");
  const [selectedMap, setSelectedMap] = useState<Map<string, PayableDocument>>(
    new Map()
  );
  const [showSelected, setShowSelected] = useState(false);
  const [payDocs, setPayDocs] = useState<PayableDocument[] | null>(null);

  const { data: companies = [] } = useCompanies();
  const { data: docs = [], isLoading } = useSupplierPayableDocuments(company);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Normalized RUT query (digits + check digit only) so "76.715.392-9", "76715392-9" and
    // "76715392" all match the canonical stored "76715392-9".
    const qRut = q.replace(/[^0-9k]/g, "");
    return docs.filter((d) => {
      if (estado !== "all" && d.estado !== estado) return false;
      if (!q) return true;
      const rutDigits = (d.supplier_rut ?? "").toLowerCase().replace(/[^0-9k]/g, "");
      return (
        d.supplier_name.toLowerCase().includes(q) ||
        (d.folio ?? "").toLowerCase().includes(q) ||
        (qRut !== "" && rutDigits.includes(qRut))
      );
    });
  }, [docs, search, estado]);

  // Total del listado = saldo neto del sub-mayor (misma convención que la columna Saldo).
  const total = useMemo(
    () => filtered.reduce((s, d) => s + displaySaldo(d), 0),
    [filtered]
  );
  const pagination = usePagination(filtered, 20);

  const selectedDocs = useMemo(
    () => Array.from(selectedMap.values()),
    [selectedMap]
  );
  // Total de la selección = efectivo neto a pagar (deuda − NC), por eso suma payable_clp sin
  // re-firmar: representa el monto del pago, no el saldo del sub-mayor que muestra la columna.
  const selectedTotal = useMemo(
    () => selectedDocs.reduce((s, d) => s + Number(d.payable_clp), 0),
    [selectedDocs]
  );
  // Solo las facturas contabilizadas (is_posted) se pueden pagar. El saldo inicial siempre lo está.
  const selectableFiltered = useMemo(
    () => filtered.filter((d) => d.is_posted),
    [filtered]
  );
  const allSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((d) => selectedMap.has(keyOf(d)));

  const toggleOne = (d: PayableDocument, on: boolean) => {
    if (on && !d.is_posted) return; // no se puede seleccionar una factura sin contabilizar
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (on) next.set(keyOf(d), d);
      else next.delete(keyOf(d));
      return next;
    });
  };
  const toggleAll = (on: boolean) =>
    setSelectedMap((prev) => {
      const next = new Map(prev);
      for (const d of filtered) {
        if (on && d.is_posted) next.set(keyOf(d), d);
        else if (!on) next.delete(keyOf(d));
      }
      return next;
    });

  // Switching empresa clears the selection (a payment nómina is single-company).
  const handleCompany = (v: string) => {
    setCompany(v);
    setSelectedMap(new Map());
  };

  return (
    <PageContainer>
      <PageHeader
        title="Pagos a Proveedor"
        description="Todas las facturas y saldos abiertos por pagar. Selecciona y genera el pago (nómina)."
        actions={
          selectedDocs.length > 0 ? (
            <Button onClick={() => setPayDocs(selectedDocs)}>
              <Wallet className="mr-2 h-4 w-4" />
              Pagar seleccionadas ({selectedDocs.length}) · $
              {formatCurrency(selectedTotal)}
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {filtered.length} documentos · ${formatCurrency(total)}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Select value={company} onValueChange={handleCompany}>
                <SelectTrigger className="h-9 w-full sm:w-56">
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={estado} onValueChange={setEstado}>
                <SelectTrigger className="h-9 w-full sm:w-44">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Estado: Todos</SelectItem>
                  <SelectItem value="vencida">Vencida</SelectItem>
                  <SelectItem value="por_vencer">Por vencer</SelectItem>
                  <SelectItem value="sin_fecha">Sin fecha</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar proveedor / RUT / folio..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 w-full pl-8"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        {selectedDocs.length > 0 && (
          <div className="border-b bg-muted/40">
            <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => setShowSelected((v) => !v)}
                className="group flex items-center gap-1.5 text-left text-sm"
                aria-expanded={showSelected}
              >
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                    showSelected ? "rotate-180" : ""
                  }`}
                />
                <span className="font-medium">
                  {selectedDocs.length}{" "}
                  {selectedDocs.length === 1 ? "documento" : "documentos"}{" "}
                  seleccionado{selectedDocs.length === 1 ? "" : "s"}
                </span>
                <span className="text-muted-foreground">
                  · ${formatCurrency(selectedTotal)}
                </span>
                <span className="ml-1 hidden text-xs text-muted-foreground sm:inline">
                  Se generará un solo comprobante con todos.
                </span>
              </button>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9"
                  onClick={() => setSelectedMap(new Map())}
                >
                  Limpiar
                </Button>
                <Button
                  size="sm"
                  className="h-9"
                  onClick={() => setPayDocs(selectedDocs)}
                >
                  <Wallet className="mr-2 h-4 w-4" />
                  Pagar seleccionadas ({selectedDocs.length})
                </Button>
              </div>
            </div>
            {showSelected && (
              <div className="max-h-64 animate-in fade-in-0 slide-in-from-top-1 overflow-auto border-t duration-200">
                <table className="w-full text-sm">
                  <tbody>
                    {selectedDocs.map((d) => (
                      <tr
                        key={keyOf(d)}
                        className="border-b border-border/60 last:border-0 hover:bg-muted/30"
                      >
                        <td className="px-4 py-2">
                          <div className="font-medium text-gray-800">
                            {d.supplier_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {d.supplier_rut ? `${formatRut(d.supplier_rut)} · ` : ""}
                            {origenLabel(d)}
                            {d.folio ? ` · Folio ${d.folio}` : ""}
                          </div>
                        </td>
                        <td
                          className={`px-4 py-2 text-right font-mono ${
                            isCredit(d) ? "text-green-700" : ""
                          }`}
                        >
                          ${formatCurrency(displaySaldo(d))}
                        </td>
                        <td className="w-0 px-4 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label="Quitar"
                            onClick={() => toggleOne(d, false)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title="Sin documentos por pagar"
              description="No hay facturas ni saldos abiertos para esta empresa / filtro."
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-0">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={(v) => toggleAll(!!v)}
                        aria-label="Seleccionar todos"
                      />
                    </TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead>Folio</TableHead>
                    <TableHead>Emisión</TableHead>
                    <TableHead>Vencimiento</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="w-0">Pago</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagination.pageItems.map((d) => (
                    <TableRow key={keyOf(d)}>
                      <TableCell>
                        <span
                          title={
                            d.is_posted
                              ? undefined
                              : "Contabiliza la factura antes de pagarla"
                          }
                        >
                          <Checkbox
                            checked={selectedMap.has(keyOf(d))}
                            onCheckedChange={(v) => toggleOne(d, !!v)}
                            disabled={!d.is_posted}
                            aria-label={`Seleccionar ${d.supplier_name}`}
                          />
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">
                        {d.supplier_rut && (
                          <div className="font-mono text-xs text-muted-foreground">
                            {formatRut(d.supplier_rut)}
                          </div>
                        )}
                        <Link
                          to={`/contabilidad/proveedores/${d.supplier_id}`}
                          className="text-primary hover:underline"
                        >
                          {d.supplier_name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {origenLabel(d)}
                        {!d.is_posted && (
                          <Badge
                            variant="secondary"
                            className="ml-2 bg-amber-100 text-amber-800"
                          >
                            Sin contabilizar
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{d.folio ?? "-"}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {d.emission_date ? formatDate(d.emission_date) : "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {d.due_date ? formatDate(d.due_date) : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={ESTADO_CHIP[d.estado]?.className}
                        >
                          {ESTADO_CHIP[d.estado]?.label ?? d.estado}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium ${
                          isCredit(d) ? "text-green-700" : ""
                        }`}
                      >
                        ${formatCurrency(displaySaldo(d))}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="h-8"
                          onClick={() => setPayDocs([d])}
                          disabled={!d.is_posted}
                          title={
                            d.is_posted
                              ? undefined
                              : "Contabiliza la factura antes de pagarla"
                          }
                        >
                          Pagar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="border-t p-4">
                <TablePagination
                  page={pagination.page}
                  pageCount={pagination.pageCount}
                  totalItems={pagination.total}
                  pageSize={20}
                  onPageChange={pagination.setPage}
                  itemLabel="documentos"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <PagarDocumentosDialog
        docs={payDocs ?? []}
        open={!!payDocs}
        onOpenChange={(o) => {
          if (!o) setPayDocs(null);
        }}
        onPaid={() => setSelectedMap(new Map())}
      />
    </PageContainer>
  );
}
